/**
 * Storage Migration Utility
 * 
 * Handles localStorage migration when switching between HTTP and HTTPS schemes.
 * Since localStorage is scheme-specific (http://localhost vs https://localhost),
 * auth data must be cleared when switching schemes to prevent auth issues.
 */

const SCHEME_STORAGE_KEY = 'app-scheme';
const AUTH_STORAGE_KEYS = [
  'auth-storage', // Zustand persist storage
  'refreshToken', // Auth service refresh token
  'guestUserId', // Guest service user ID
  'guestToken', // Guest service token
  'guestRefreshToken', // Guest service refresh token
] as const;

/**
 * Get the current protocol scheme from window.location
 */
function getCurrentScheme(): 'http' | 'https' {
  const protocol = window.location.protocol;
  return protocol === 'https:' ? 'https' : 'http';
}

/**
 * Get the stored scheme from localStorage
 */
function getStoredScheme(): 'http' | 'https' | null {
  const stored = localStorage.getItem(SCHEME_STORAGE_KEY);
  if (stored === 'http' || stored === 'https') {
    return stored;
  }
  return null;
}

/**
 * Store the current scheme in localStorage
 */
function storeScheme(scheme: 'http' | 'https'): void {
  localStorage.setItem(SCHEME_STORAGE_KEY, scheme);
}

/**
 * Clear all auth-related localStorage items
 */
function clearAuthStorage(): void {
  console.log('[StorageMigration] Clearing auth-related storage due to scheme change');
  
  AUTH_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
      console.log(`[StorageMigration] Removed: ${key}`);
    } catch (error) {
      console.warn(`[StorageMigration] Failed to remove ${key}:`, error);
    }
  });
}

/**
 * Initialize storage migration
 * 
 * This function should be called on app startup, before any auth restoration.
 * It detects scheme changes (HTTP ↔ HTTPS) and clears auth data if the scheme changed.
 */
export function initializeStorageMigration(): void {
  try {
    const currentScheme = getCurrentScheme();
    const storedScheme = getStoredScheme();
    
    console.log('[StorageMigration] Checking scheme:', {
      current: currentScheme,
      stored: storedScheme,
    });
    
    // If we have a stored scheme and it differs from current, clear auth storage
    if (storedScheme && storedScheme !== currentScheme) {
      console.log(`[StorageMigration] Scheme changed from ${storedScheme} to ${currentScheme}, clearing auth data`);
      clearAuthStorage();
    }
    
    // Store current scheme for next check
    storeScheme(currentScheme);
    console.log('[StorageMigration] Storage migration complete');
  } catch (error) {
    console.error('[StorageMigration] Error during storage migration:', error);
    // Don't throw - migration errors shouldn't break the app
  }
}
